// ─── Écran Messagerie (Core) ──────────────────────────────────────
// Monté par l'app depuis la tuile Famille. Deux niveaux : la LISTE des
// discussions d'une cible, puis le FIL de l'une d'elles.
//
// `cible` est fournie par l'app : My Calendar passe toujours
// `calendrier:<calIdActif>` — elle n'expose pas le rattachement à l'espace
// (cf. `ciblesCreables` dans services/cibleDiscussion.js).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import PageHeader from '../ui/PageHeader';
import LigneSwipe from '../ui/LigneSwipe';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { confirmerSuppression } from '../services/confirm';
import {
  ecouterDiscussionsMulti, assurerGenerale, fermerDiscussion, supprimerDiscussion,
} from '../services/discussions';
import { peutAdministrerDiscussion, peutCreerDiscussion } from '../services/cibleDiscussion';
import FilMessages from './FilMessages';
import FeuilleDiscussion from './FeuilleDiscussion';

// `cibles` : TOUS les porteurs que l'utilisateur voit — [{ cle, nom }]. La
// messagerie n'est plus bornée au calendrier ACTIF : on a constaté le 10/08
// qu'ouvrir la messagerie depuis un calendrier sans conversation donnait un
// écran vide, sans que rien n'indique qu'il fallait changer de calendrier.
// `cible` reste la cible de CRÉATION (le calendrier actif).
export default function EcranMessagerie({ cible, cibles, membres = [], proprietaireEspace, onRetour }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const uid = user?.uid || null;
  const s = useMemo(() => styles(UI), [UI]);
  const [discussions, setDiscussions] = useState([]);
  const [ouverte, setOuverte] = useState(null);
  const [formulaire, setFormulaire] = useState(false);
  // Une erreur d'écoute ou de création NE DOIT PAS se traduire par une liste
  // vide muette : « refusé par les règles », « échec réseau » et « aucune
  // discussion » donnaient le même écran, ce qui a rendu le diagnostic
  // impossible depuis l'app le 09/08.
  const [erreur, setErreur] = useState('');

  // La Générale d'un calendrier existe TOUJOURS. Elle est posée à la CRÉATION
  // du calendrier ; cet appel n'est que le RATTRAPAGE des calendriers plus
  // anciens. Id déterministe → deux appareils qui l'ouvrent en même temps
  // écrivent le même document, donc pas de doublon.
  useEffect(() => {
    if (!cible || !uid) return;
    assurerGenerale(cible, {
      uid, nom: user?.prenom || user?.email || '',
      titre: t('messagerie.general'), throwSiEchec: true,
    }).catch(e => setErreur(e?.message || String(e)));
  }, [cible, uid]);

  // Union d'écoutes, une par porteur. SURTOUT PAS une requête transverse :
  // elle échouerait en bloc sur le premier porteur non visible.
  const listeCibles = useMemo(
    () => (cibles && cibles.length ? cibles : (cible ? [{ cle: cible, nom: '' }] : [])),
    [cibles, cible],
  );
  const nomDe = useMemo(() => {
    const m = {};
    listeCibles.forEach(c => { m[c.cle] = c.nom || ''; });
    return m;
  }, [listeCibles]);

  useEffect(() => {
    setErreur('');
    const cles = listeCibles.map(c => c.cle);
    if (!cles.length || !uid) { setDiscussions([]); return undefined; }
    return ecouterDiscussionsMulti(cles, uid, setDiscussions, setErreur);
  }, [listeCibles.map(c => c.cle).join('|'), uid]);

  // La liste se rafraîchit en direct : on resynchronise la discussion ouverte
  // pour que sa fermeture (ou un changement de sélection) s'y reflète.
  const discOuverte = ouverte ? discussions.find(d => d.id === ouverte.id) || ouverte : null;

  const peutCreer = peutCreerDiscussion(cible, uid, { proprietaireEspace, voitCible: true });

  const administrer = (d) => {
    const present = !!membres.find(m => m.uid === d.creePar);
    return peutAdministrerDiscussion(d, uid, { proprietaireEspace, createurPresent: present });
  };

  if (discOuverte) {
    return (
      <View style={s.flex}>
        <PageHeader
          titre={discOuverte.titre}
          onRetour={() => setOuverte(null)}
          action={administrer(discOuverte) && !discOuverte.ferme ? {
            icone: '🔒', label: t('messagerie.fermerDiscussion'),
            onPress: () => confirmerSuppression(
              t('messagerie.fermerDiscussion'), t('messagerie.confirmFermer'),
              () => fermerDiscussion(discOuverte, uid).catch(() => {}),
            ),
          } : undefined}
        />
        <FilMessages disc={discOuverte} />
      </View>
    );
  }

  return (
    <View style={s.flex}>
      <PageHeader
        titre={t('messagerie.titre')}
        onRetour={onRetour}
        action={peutCreer ? {
          icone: '＋', label: t('messagerie.nouvelleDiscussion'),
          onPress: () => setFormulaire(true),
        } : undefined}
      />

      <ScrollView contentContainerStyle={s.contenu}>
        {/* La cause AVANT la liste : une liste vide par refus n'est pas une
            liste vide. */}
        {!!erreur && (
          <View style={s.alerte}>
            <Text style={s.alerteTxt}>⚠️ {t('messagerie.erreurLecture')}</Text>
            <Text style={s.alerteDet}>{erreur}</Text>
          </View>
        )}

        {/* Sans cible (aucun calendrier actif), rien n'a MÊME été tenté : le
            dire, plutôt que de laisser croire à une messagerie vide. */}
        {!cible && <Text style={s.vide}>{t('messagerie.sansCalendrier')}</Text>}

        {!!cible && discussions.length === 0 && !erreur && (
          <Text style={s.vide}>{t('messagerie.aucuneDiscussion')}</Text>
        )}

        {discussions.map((d) => {
          const ligne = (
            <TouchableOpacity style={s.ligne} activeOpacity={0.7} onPress={() => setOuverte(d)}>
              <Text style={s.emoji}>{d.generale ? '💬' : '🗂️'}</Text>
              <View style={s.fil}>
                <Text style={s.nom} numberOfLines={1}>{d.titre}</Text>
                <Text style={s.sous}>
                  {/* Le CALENDRIER d'abord : sans lui, deux « Général » se
                      ressemblent et on ne sait pas lequel on ouvre. */}
                  {nomDe[d.cle] ? `${nomDe[d.cle]} · ` : ''}
                  {d.visibleATous || d.generale ? t('messagerie.visibleParTous')
                    : t('messagerie.restreinte')}
                  {d.ferme ? ` · ${t('messagerie.fermee')}` : ''}
                </Text>
              </View>
            </TouchableOpacity>
          );
          // La Générale ne se supprime pas : pas de swipe dessus.
          return d.generale || !administrer(d) ? (
            <View key={d.id}>{ligne}</View>
          ) : (
            <LigneSwipe
              key={d.id}
              espacement={7}
              onSupprimer={() => confirmerSuppression(
                t('commun.supprimer'), t('messagerie.confirmSupprimerDiscussion'),
                () => supprimerDiscussion(d).catch(() => {}),
              )}
            >
              {ligne}
            </LigneSwipe>
          );
        })}
      </ScrollView>

      {formulaire && (
        <FeuilleDiscussion
          cible={cible}
          membres={membres.filter(m => m.uid !== uid)}
          onFerme={() => setFormulaire(false)}
        />
      )}
    </View>
  );
}

const styles = (UI) => StyleSheet.create({
  // `backgroundColor` OBLIGATOIRE : l'app monte cet écran en calque plein écran
  // PAR-DESSUS le hub Famille (StyleSheet.absoluteFill). Sans fond, il est
  // transparent et les tuiles du hub se voient au travers — constaté par Cédric
  // le 09/08, capture à l'appui. Même contrat que EcranLieux / EcranContacts /
  // EcranJournal, qui posent tous `{ flex: 1, backgroundColor: UI.bg }`.
  flex: { flex: 1, backgroundColor: UI.bg },
  // `fil` : le même conteneur SANS fond, pour les blocs internes qui ne font
  // qu'occuper l'espace restant d'une ligne (sinon on repeindrait par-dessus).
  fil: { flex: 1 },
  contenu: { padding: 12 },
  alerte: {
    backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5,
    borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10,
  },
  alerteTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  alerteDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
  vide: { fontSize: 13, color: UI.faint, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },
  ligne: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    borderRadius: 10, borderWidth: 1, borderColor: UI.border,
    backgroundColor: UI.champ, padding: 11, marginBottom: 7,
  },
  emoji: { fontSize: 20 },
  nom: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  sous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
});
